import React, { useState } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const fmt = (n) => !isFinite(n) ? '—' : (Math.round(n * 10000) / 10000).toLocaleString('en-IN');

const PercentageCalculator = () => {
  // Mode 1: X% of Y
  const [pctA, setPctA] = useState(15);
  const [valA, setValA] = useState(1200);
  const r1 = (pctA / 100) * valA;

  // Mode 2: X is what % of Y
  const [xB, setXB] = useState(30);
  const [yB, setYB] = useState(150);
  const r2 = (xB / yB) * 100;

  // Mode 3: % increase/decrease
  const [fromC, setFromC] = useState(100);
  const [toC, setToC] = useState(125);
  const r3 = ((toC - fromC) / fromC) * 100;

  // Mode 4: GST calc (India-specific)
  const [baseD, setBaseD] = useState(1000);
  const [gstD, setGstD] = useState(18);
  const [gstMode, setGstMode] = useState('add');
  const gstAmount = gstMode === 'add'
    ? (baseD * gstD) / 100
    : baseD - (baseD * 100) / (100 + gstD);
  const gstTotal = gstMode === 'add' ? baseD + gstAmount : baseD;
  const gstBase = gstMode === 'add' ? baseD : baseD - gstAmount;

  return (
    <CalculatorLayout
      title="Percentage Calculator"
      subtitle="Calculate percentages, increases, decreases — plus India's GST in one tool."
      category="Math"
      description={<p>A versatile percentage calculator for everyday use. Switch between modes to calculate <strong>X% of Y</strong>, <strong>X is what % of Y</strong>, <strong>percentage change</strong>, and <strong>GST</strong> (add or remove, at 5/12/18/28% slabs).</p>}
      formulaNote="Percentage = (part / whole) × 100   •  GST added: final = base × (1 + rate/100)"
    >
      <Tabs defaultValue="of" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full">
          <TabsTrigger value="of">% of a number</TabsTrigger>
          <TabsTrigger value="what">What %</TabsTrigger>
          <TabsTrigger value="change">% Change</TabsTrigger>
          <TabsTrigger value="gst">GST (India)</TabsTrigger>
        </TabsList>

        <TabsContent value="of" className="mt-5">
          <Row>
            <FieldSmall label="What is" value={pctA} onChange={setPctA} suffix="%" />
            <span className="text-slate-500">of</span>
            <FieldSmall value={valA} onChange={setValA} />
            <span className="text-slate-500">?</span>
          </Row>
          <ResultBlock label={`${pctA}% of ${valA}`} value={fmt(r1)} />
        </TabsContent>

        <TabsContent value="what" className="mt-5">
          <Row>
            <FieldSmall value={xB} onChange={setXB} />
            <span className="text-slate-500">is what percent of</span>
            <FieldSmall value={yB} onChange={setYB} />
            <span className="text-slate-500">?</span>
          </Row>
          <ResultBlock label={`${xB} is ${fmt(r2)}% of ${yB}`} value={fmt(r2) + '%'} />
        </TabsContent>

        <TabsContent value="change" className="mt-5">
          <Row>
            <span className="text-slate-500">From</span>
            <FieldSmall value={fromC} onChange={setFromC} />
            <span className="text-slate-500">to</span>
            <FieldSmall value={toC} onChange={setToC} />
          </Row>
          <ResultBlock
            label={r3 >= 0 ? 'Increase' : 'Decrease'}
            value={`${r3 >= 0 ? '+' : ''}${fmt(r3)}%`}
            tone={r3 >= 0 ? 'green' : 'saffron'}
          />
        </TabsContent>

        <TabsContent value="gst" className="mt-5">
          <div className="flex gap-2 mb-4">
            <button onClick={() => setGstMode('add')} className={`px-4 py-2 rounded-md text-sm font-medium border ${gstMode === 'add' ? 'bg-saffron-500 text-white border-saffron-500' : 'bg-white text-slate-700 border-slate-200'}`}>Add GST</button>
            <button onClick={() => setGstMode('remove')} className={`px-4 py-2 rounded-md text-sm font-medium border ${gstMode === 'remove' ? 'bg-saffron-500 text-white border-saffron-500' : 'bg-white text-slate-700 border-slate-200'}`}>Remove GST</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm mb-1.5 block">{gstMode === 'add' ? 'Base Amount (₹)' : 'Total Amount incl. GST (₹)'}</Label>
              <Input type="number" value={baseD} onChange={(e) => setBaseD(+e.target.value)} />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">GST Slab</Label>
              <div className="flex gap-2 flex-wrap">
                {[5, 12, 18, 28].map(s => (
                  <button key={s} onClick={() => setGstD(s)} className={`px-3 py-1.5 rounded-md text-sm border ${gstD === s ? 'bg-indgreen-500 text-white border-indgreen-500' : 'bg-white border-slate-200 text-slate-700'}`}>
                    {s}%
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <ResultBox label="Base" value={'₹' + fmt(gstBase)} />
            <ResultBox label={`GST @ ${gstD}%`} value={'₹' + fmt(gstAmount)} tone="saffron" />
            <ResultBox label="Total" value={'₹' + fmt(gstTotal)} tone="green" />
          </div>
        </TabsContent>
      </Tabs>
    </CalculatorLayout>
  );
};

const Row = ({ children }) => (
  <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">{children}</div>
);
const FieldSmall = ({ label, value, onChange, suffix }) => (
  <div className="inline-flex flex-col gap-1">
    {label && <span className="text-xs text-slate-500">{label}</span>}
    <div className="relative">
      <Input type="number" value={value} onChange={(e) => onChange(+e.target.value)} className="w-28" />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{suffix}</span>}
    </div>
  </div>
);
const ResultBlock = ({ label, value, tone }) => (
  <div className={`mt-5 p-5 rounded-xl border ${
    tone === 'green' ? 'bg-indgreen-50 border-indgreen-200' : tone === 'saffron' ? 'bg-saffron-50 border-saffron-200' : 'bg-slate-50 border-slate-200'
  }`}>
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-display text-3xl md:text-4xl font-bold text-bharat-navy display-font mt-1">{value}</div>
  </div>
);
const ResultBox = ({ label, value, tone }) => {
  const bg = tone === 'green' ? 'bg-indgreen-50 border-indgreen-200' : tone === 'saffron' ? 'bg-saffron-50 border-saffron-200' : 'bg-slate-50 border-slate-200';
  const text = tone === 'green' ? 'text-indgreen-700' : tone === 'saffron' ? 'text-saffron-700' : 'text-bharat-navy';
  return (
    <div className={`p-4 rounded-lg border ${bg}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font text-xl font-bold ${text} mt-1`}>{value}</div>
    </div>
  );
};

export default PercentageCalculator;
